import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@connected/ui/components/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@connected/ui/components/select';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Bell, Database, LogOut, Settings, User } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import logo from '@/assets/logo.png';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import ArrowCircleBrokenUpRight from '@/components/icons/arrow-circle-broken-up-right';
import MarkerPin from '@/components/icons/marker-pin';
import { LANGUAGES, SUPPORTED_LANGUAGES } from '@/config/locales';
import { useLanguageSwitcher } from '@/hooks/use-language';

export function CustomerNavbar({
    isPublic = false,
}: {
    isPublic?: boolean;
}) {
    const auth = useAuthentication();
    const { t } = useTranslation('auth');
    const params = useParams({ from: !isPublic ? '/_auth' : '/_public/customer/locations/$locationId/electrical/projects/$projectId' });
    const [locale, setLocale] = useLanguageSwitcher();
    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    const locationId = 'locationId' in params ? params.locationId : null;
    const projectId = 'projectId' in params ? params.projectId : null;

    const objectQuery = useQuery({
        ...portaalQueryOptions.portal.objects.view({
            ObjectId: locationId,
        }),
        enabled: !!auth.isAuthenticated && !!locationId,
    });

    const projectQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({
            ElectricalProjectId: projectId,
        }),
        enabled: !auth.isAuthenticated && !!projectId,
    });

    // TODO: remove when user login works
    const userProfilePicture = ''; // test image: https://i.imgur.com/VM4Xf0X.png

    return (
        <nav className="flex justify-center bg-background-primary w-full border-b border-border">
            <div className="flex flex-wrap items-center justify-between h-full w-full py-4 px-4 sm:px-8 max-w-screen-2xl">
                <div className="flex items-center">
                    {/* Logo */}
                    <Link to="/">
                        <img src={logo} className="h-6 object-fill" />
                        {/* May anyone want the "Connected" text in the logo to disappear at small screen widths use code underneath */}
                        {/* <img
                            src={logoIcon}
                            className="h-6 object-fill sm:hidden min-w-12"
                        /> */}
                    </Link>

                    {/* Current location */}
                    {(objectQuery.data || projectQuery.data) && (
                        <div className="ms-6 text-sm flex justify-start items-center text-text-primary/60">
                            <MarkerPin className="min-w-4" />
                            {/* TODO: remove when locations can be requested in the backend by ID */}
                            <span className="ms-4">
                                {
                                    auth.isAuthenticated
                                        ? `${objectQuery.data?.Object?.Locality} ${objectQuery.data?.Object?.PostalCode}, ${objectQuery.data?.Object?.Street} ${objectQuery.data?.Object?.StreetNumber}`
                                        : `${projectQuery.data?.Project?.ProjectObject?.Address?.Locality} ${projectQuery.data?.Project?.ProjectObject?.Address?.PostalCode}, ${projectQuery.data?.Project?.ProjectObject?.Address?.Street} ${projectQuery.data?.Project?.ProjectObject?.Address?.StreetNumber}`
                                }
                            </span>
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-4">
                    {auth.isAuthenticated && (
                        <>
                            {/* Check button */}
                            <Button
                                variant="link"
                                className="flex items-center gap-1 border px-3 rounded-lg text-foreground hover:no-underline hover:bg-background max-sm:hidden"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                <Database className="w-5 h-5" />
                                {t('navbar.check')}
                            </Button>
                        </>
                    )}

                    {auth.isAuthenticated && (
                        <>
                            {/* Notifications button */}
                            <Button
                                variant="link"
                                className="text-foreground hover:no-underline rounded-full px-2.5 hover:bg-background"
                                onClick={() => setComingSoonModalOpen(true)}
                            >
                                <Bell className="w-5 h-5" />
                            </Button>
                        </>
                    )}

                    {/* Language switcher */}
                    <Select
                        value={locale}
                        onValueChange={(value) => {
                            const selectedLanguage = SUPPORTED_LANGUAGES.find(language => language === value);
                            if (selectedLanguage) {
                                setLocale(selectedLanguage);
                            }
                        }}
                    >
                        <SelectTrigger className="w-min justify-start gap-2 bg-transparent hover:bg-background">
                            {/* Show only flag in SelectValue */}
                            <SelectValue>
                                {(() => {
                                    const currentLanguage = LANGUAGES.find(lang => lang.code === locale);
                                    return currentLanguage ? <span className={`fi fi-${currentLanguage.code}`} /> : null;
                                })()}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {LANGUAGES
                                .map(language => (
                                    <SelectItem
                                        className="pl-2 pr-8 focus:bg-background focus:text-background-foreground"
                                        iconClassName="left-auto right-2"
                                        key={language.code}
                                        value={language.code}
                                    >
                                        <span className={`fi fi-${language.code}`} />
                                        {' '}
                                        <span>{language.name}</span>
                                    </SelectItem>
                                ))}
                        </SelectContent>
                    </Select>

                    {auth.isAuthenticated && (
                        <>
                            {/* Account button */}
                            <DropdownMenu>
                                <DropdownMenuTrigger className="flex justify-center items-center outline-none text-foreground hover:no-underline p-0 w-10 h-10 overflow-hidden select-none">
                                    {/* TODO: rework when user login works */}
                                    {userProfilePicture
                                        ? (
                                                <div className="rounded-full border">
                                                    <img
                                                        src={userProfilePicture}
                                                        className="w-full h-full object-cover rounded-full"
                                                    />
                                                </div>
                                            )
                                        : (
                                                <div className="flex justify-center items-center bg-background-secondary rounded-full border h-full w-full">
                                                    <User className="w-5 h-5" />
                                                </div>
                                            )}
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="min-w-60" align="end">
                                    <DropdownMenuLabel className="flex justify-start gap-3 items-center">
                                        {/* relative is necessary to contain the absolute position of the child within this container */}
                                        <div className="relative text-foreground hover:no-underline p-0 w-10 h-10 overflow-hidden select-none">
                                            {/* TODO: rework when user login works */}
                                            {userProfilePicture
                                                ? (
                                                        <div className="rounded-full border">
                                                            <img
                                                                src={userProfilePicture}
                                                                className="w-full h-full object-cover rounded-full"
                                                            />
                                                        </div>
                                                    )
                                                : (
                                                        <div className="flex justify-center items-center bg-background-secondary rounded-full border h-full w-full">
                                                            <User className="w-5 h-5" />
                                                        </div>
                                                    )}
                                            {/* absolute is necessary because the image and the online status dot overlap with each other */}
                                            {/* TODO: custom color doesn't work yet, remove fixed green color */}
                                            <div className="absolute bottom-0 right-0 bg-success-secondary bg-emerald-600 rounded-full h-2.5 w-2.5"></div>
                                        </div>
                                        <div className="flex flex-col">
                                            {/* TODO: remove when user login works */}
                                            {auth.currentUser.DisplayName}
                                            <span className="font-light whitespace-pre-line">
                                                {auth.currentUser.Roles?.map(role => role.Name).join('\n')}
                                            </span>
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator className="bg-border" />

                                    {/* TODO: create functionality for these 4 buttons underneath */}
                                    {/* Account settings button */}
                                    <DropdownMenuItem className="px-3 py-2 font-bold focus:bg-background-secondary focus:text-popover-foreground cursor-pointer" asChild>
                                        <Link to="/dashboard/account/$userId/profile" params={{ userId: auth.currentUser!.UserId }}>
                                            <Settings className="w-5 h-5" />
                                            {' '}
                                            {t('navbar.profile-dropdown.account-settings')}
                                        </Link>
                                    </DropdownMenuItem>

                                    {(auth.currentUser?.Organization?.ElectricalOrganizationKinds?.includes('Inspection') || auth?.currentUser?.IsAdmin) && (
                                        <>
                                            {/* Dashboard button */}
                                            <Link to="/dashboard">
                                                <DropdownMenuItem className="px-3 py-2 font-bold focus:bg-background-secondary focus:text-popover-foreground cursor-pointer">
                                                    <ArrowCircleBrokenUpRight className="w-5 h-5" />
                                                    {' '}
                                                    {t('navbar.profile-dropdown.dashboard')}
                                                </DropdownMenuItem>
                                            </Link>
                                        </>
                                    )}

                                    {/* Logout button */}
                                    <DropdownMenuSeparator className="bg-border" />
                                    <Link to="/logout">
                                        <DropdownMenuItem
                                            className="px-3 py-2 font-bold focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                        >
                                            <LogOut className="w-5 h-5" />
                                            {' '}
                                            {t('navbar.profile-dropdown.logout')}
                                        </DropdownMenuItem>
                                    </Link>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    )}
                </div>
            </div>
            <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
        </nav>
    );
}
